import React from 'react';
import { getTitleColor } from '../../data/bookTitles';

interface TitleUnlockCelebrationProps {
    isVisible: boolean;
    onClose: () => void;
    bookNumber: number;
    title: string;
}

/**
 * Componente que celebra cuando un niño desbloquea un nuevo título
 */
const TitleUnlockCelebration: React.FC<TitleUnlockCelebrationProps> = ({
    isVisible,
    onClose,
    bookNumber,
    title
}) => {
    if (!isVisible) return null;

    const colorClass = getTitleColor(bookNumber);
    const emojis = {
        1: '👑', // Ministro de la Verdad
        2: '🧭', // Peregrino Pensador
        3: '🛡️', // Defensor del Bien Común
        4: '🏛️', // Subalcalde
        5: '🔄', // Maestro de la Transformación
        6: '📋', // Jefe de Proyectos
        7: '💪', // Maestro de la Vitalidad
        8: '💰'  // Director de Desarrollo y Financiación
    };

    const emoji = emojis[bookNumber as keyof typeof emojis] || '🏆';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
                {/* Header con gradiente del título */}
                <div className={`bg-gradient-to-r ${colorClass} p-6 text-center text-white`}>
                    <div className="text-6xl mb-4 animate-bounce">
                        {emoji}
                    </div>
                    <h2 className="text-2xl font-bold mb-2">
                        ¡Nuevo Título Desbloqueado!
                    </h2>
                    <p className="text-lg opacity-90">
                        Has completado todas las actividades del Libro {bookNumber}
                    </p>
                </div>

                {/* Contenido principal */}
                <div className="p-6 text-center">
                    <div className="mb-6">
                        <h3 className="text-3xl font-bold text-gray-800 mb-3">
                            {title}
                        </h3>
                        <p className="text-gray-600 text-lg">
                            ¡Felicidades! Has demostrado excelencia en todas las actividades de este libro.
                        </p>
                    </div>

                    {/* Efectos visuales */}
                    <div className="relative mb-6">
                        <div className="w-24 h-24 mx-auto bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white text-4xl animate-pulse">
                            🎉
                        </div>
                        {/* Partículas de celebración */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-0 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
                            <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-blue-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                            <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-green-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                            <div className="absolute bottom-0 right-1/3 w-2 h-2 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '1.5s' }}></div>
                        </div>
                    </div>

                    {/* Mensaje motivacional */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
                        <p className="text-blue-800 font-medium">
                            "El conocimiento es poder, y tú lo has demostrado con creces. ¡Sigue explorando y descubriendo!"
                        </p>
                    </div>

                    {/* Botón de cerrar */}
                    <button
                        onClick={onClose}
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 px-6 rounded-xl hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                    >
                        ¡Entendido!
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TitleUnlockCelebration;
